// Role-gate helpers. Read once at the call site — checks the roles[] array
// from the session cookie (populated by /__app/me).
//
// Convention (from user spec):
//   - User-level role:    "employee" | "doctor"
//   - Employee table has its own "Admin" role (surfaced into roles[] as "admin"
//     by the middleware's case-insensitive normalization).
//
// Clinical-edit allowed for any logged-in employee (covers doctor, admin,
// and regular front-office). The app's REQUIRED_ROLE gate already keeps
// non-employees out at login.
//
// NOTE: this coarse helper still gates the *patient-info* page edit
// (PatientInfo.jsx). The per-section chart permissions live in
// `canEditSection()` below.
import { TOOTH_TAXI_BRANCH_ID } from '../data/toothTaxiPreset.js'
import { parseDateObj } from './chart-helpers.js'

export function canEditClinical(me) {
  if (!me) return false
  const roles = Array.isArray(me.roles) ? me.roles : []
  return roles.includes('employee') || roles.includes('doctor') || roles.includes('admin')
}

export function isEmployee(me) {
  return Array.isArray(me?.roles) && me.roles.includes('employee')
}

export function isDoctor(me) {
  return Array.isArray(me?.roles) && me.roles.includes('doctor')
}

// A doctor-scoped session: came in via the doctor_portal SSO hand-off (role
// `doctor`, NOT `employee`). These sessions see ONLY the patient Chart menu and
// have patient contact info hidden. Employees who also hold a doctor role keep
// full access (they are not "doctor-only").
export function isDoctorOnly(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.includes('doctor') && !roles.includes('employee')
}

export function isAdmin(me) {
  return Array.isArray(me?.roles) && me.roles.includes('admin')
}

// Manager-area access. The Manager menu (and everything under it, e.g. Expense
// accounting) is restricted to employees holding a Manager, GM, Stock Manager,
// or Admin role. GM (General Manager) is wired everywhere `manager` is. Role strings come from the employee record's `role` array, already
// lower-cased by the middleware; we further strip spaces/underscores/hyphens so
// "Stock Manager" / "stock_manager" / "stockmanager" all match.
const MANAGER_ROLES = new Set(['manager', 'gm', 'stockmanager', 'admin'])
const normRole = (r) => String(r || '').toLowerCase().replace(/[\s._-]+/g, '')

export function canManage(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => MANAGER_ROLES.has(normRole(r)))
}

// ตั้งค่าโปรโมชั่น (/manager/promotion) — WHO MAY LOOK. Manager / GM / Admin. Stock Manager
// is deliberately OUT even though canManage() lets them into the Manager area: the promotion
// catalog is the clinic's price list and its doctor-fee split, which has nothing to do with
// stock. Re-checked in functions/api/promotion-manage.js and again in Xano
// (evaluation_actor.is_manager, which is the same three roles) — keep the three in sync.
const PROMOTION_VIEW_ROLES = new Set(['manager', 'gm', 'admin'])

export function canViewPromotion(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => PROMOTION_VIEW_ROLES.has(normRole(r)))
}

// WHO MAY REWRITE A CLOSED DAY'S BILLING. A billing line is frozen outside its
// treatment's own Bangkok day; Admin is the only exception (owner's call,
// 2026-08-15 — it is what Bubble has enforced for years).
//
// Deliberately TIGHTER than canManage() and tighter than the manager/GM/admin
// gate in functions/api/treatments/[id].js: a closed day's billing is what the
// cash drawer, the revenue report and the doctor's payslip were reconciled
// against. isAdmin() would give the same answer today — this exists as its own
// name so the policy can move without dragging every other admin check with it.
//
// ⚠️ The ENFORCING copy is functions/api/billing/_guard.js#canEditPast. This one
// only shapes the screen. Change one, change the other.
export function canEditPastBilling(me) {
  return isAdmin(me)
}

// ตั้งค่าโปรโมชั่น — WHO MAY WRITE. GM / Admin only, per user spec: a Manager reads the
// catalog, only a GM adds one, edits one or takes one out of service. Narrower than the
// page gate on purpose — editing a pattern[] re-prices treatments for every patient
// mid-course on that promotion. Mirrored by the proxy (gm|admin on POST/PATCH) and by
// Xano's evaluation_actor.is_gm, which is the layer that actually enforces.
const PROMOTION_EDIT_ROLES = new Set(['gm', 'admin'])

export function canEditPromotion(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => PROMOTION_EDIT_ROLES.has(normRole(r)))
}

// ขอแก้ยอดเบิก — correcting what a claim line was worth, on the นำเข้ารายงานตามเบิกประกัน
// run page. Two gates, and the split IS the feature: a manager asks, a GM or Admin agrees.
//
// ⚠️ REQUEST is narrower than canManage — Stock Manager is deliberately OUT, the same
// argument functions/api/treatments/[id].js makes for its MANAGER_ONLY set: moving money
// between what a payer owes and what the clinic booked is not a stock concern.
// ⚠️ APPROVE is the same five people as canEditPromotion (gm|admin), the app's existing
// "this changes what other records mean" tier.
// ⚠️ Self-approval is ALLOWED (owner, 2026-08-25) — five people hold these roles and two
// are the owners. The record is the deterrent, not the ceremony: created_by and decided_by
// are stored separately and the row says "อนุมัติเอง" when they match.
//
// ⚠️ THE ENFORCING COPIES ARE functions/lib/claimAmountRequest.js#mayRequest / #mayApprove,
// re-checked in both Pages Functions. These two only shape the screen.
// CHANGE ONE, CHANGE THE OTHER.
const AMOUNT_REQUEST_ROLES = new Set(['manager', 'gm', 'admin'])
const AMOUNT_APPROVE_ROLES = new Set(['gm', 'admin'])

export function canRequestAmountFix(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => AMOUNT_REQUEST_ROLES.has(normRole(r)))
}

export function canApproveAmountFix(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => AMOUNT_APPROVE_ROLES.has(normRole(r)))
}

// เปลี่ยนสถานะรายการตามเบิก — WHO MAY SAY A CLAIM LINE WAS PAID. GM / Admin, per the
// owner, 2026-09-14. This TIGHTENS NOTHING that was ever loose-but-guarded: before it the
// answer was "anyone with a session" — the two bulk buttons on /reports/reimbursement
// carried no role check at all, SsoImport.jsx never read `me`, and the proxy checked only
// that a session existed. A manual flip also writes no audit record anywhere, so the gate
// is the only control on it.
//
// ⚠️ SIX PEOPLE, NOT TWO — `gm` is held by four branch managers as well as the two owners
// (role.js:144-148 records the measurement). Confirmed as intended, 2026-09-14.
//
// ⚠️ THE ENFORCING COPY IS functions/lib/claimStatusRole.js#maySetClaimStatus, re-checked
// in billing/status.js, billing/write-off.js, claim-import/run/[id]/commit.js and
// sso/patient-backfill.js. This one only shapes the screen. CHANGE ONE, CHANGE THE OTHER.
const CLAIM_STATUS_ROLES = new Set(['gm', 'admin'])

export function canSetClaimStatus(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => CLAIM_STATUS_ROLES.has(normRole(r)))
}

// ปิดรายการ (รับบางส่วน / เบิกไม่ได้) — ADMIN ONLY, owner 2026-09-15. Narrower than
// canSetClaimStatus ON PURPOSE: that one also gates the ตามเบิก import link and the ปกส.
// importer, which the four non-admin GMs run as daily work, and narrowing the shared set
// would have taken that away from them without anyone asking.
//
// ⚠️ MEASURED 2026-09-15 across all 114 active employees — admin is TWO people (employee 1
// and employee 40), gm is five. This moves ปิดรายการ from five people to two.
//
// ⚠️ THE ENFORCING COPY IS functions/lib/claimStatusRole.js#mayWriteOffClaim, re-checked in
// functions/api/billing/write-off.js. This one only shapes the screen, and
// src/lib/claimRole.test.mjs fails if the two sets drift.
// CHANGE ONE, CHANGE THE OTHER.
const CLAIM_WRITE_OFF_ROLES = new Set(['admin'])

export function canWriteOffClaim(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => CLAIM_WRITE_OFF_ROLES.has(normRole(r)))
}

// Access to the พนักงาน / Employee-HR section (/manager/employee). Manager, GM,
// HR or Admin — NOTE: Stock Manager is deliberately OUT (unlike canManage), and HR
// is IN (HR is not a "manager"). The `hr` role string comes from the employee
// record's `role` array ("HR"), already lower-cased by the middleware. Re-checked
// server-side in _middleware.js for the /manager/employee page + /api/hr/* proxies.
const STAFF_ROLES = new Set(['manager', 'gm', 'hr', 'admin'])

export function canAccessStaff(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => STAFF_ROLES.has(normRole(r)))
}

// หนังสือเตือน — WHO MAY ISSUE ONE, AND WHO MAY SEE THE REGISTER. HR / GM / Admin,
// per the owner's brief. Deliberately NOT canAccessStaff: a plain branch Manager
// holding no GM role is out, because a warning is a personnel record rather than
// roster information, and the register names everyone in the company.
//
// ⚠️ COUNT THE PEOPLE BEFORE TRUSTING THIS NAME. `gm` is held by four branch
// managers as well as the two owners — measured 2026-08-20 against all 114 active
// employees — so this set is SIX people, not two. If the intent was ever "the
// owners plus HR", drop 'gm' here, drop it from functions/api/hr/warning.js, and
// change the Xano precondition on POST/PATCH /employee_warning to `$me.is_hr ==
// true` alone. All three have to move together.
//
// Re-checked in functions/api/hr/warning{,-upload}.js and again in Xano
// (evaluation_actor.is_hr || is_gm), which is the layer that actually enforces —
// those endpoints are reachable with any valid Xano token, no proxy in front.
const WARNING_ROLES = new Set(['hr', 'gm', 'admin'])

export function canIssueWarning(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => WARNING_ROLES.has(normRole(r)))
}

// Manager › ทันตแพทย์ WRITES — editing a doctor's record, and creating one.
// Stock Manager is admitted to the Manager AREA by canManage() but has no business
// rewriting a doctor, so this is the narrower set. functions/api/doctors.js (POST)
// and functions/api/doctors/[id].js (PATCH) enforce the same three; Xano narrows
// the create further still, to the literal "Admin"/"Manager" on the caller's
// Employee.role array — keep all of them in mind before widening this.
const DENTIST_EDIT_ROLES = new Set(['manager', 'gm', 'admin'])

export function canEditDentist(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => DENTIST_EDIT_ROLES.has(normRole(r)))
}

// HR / Admin only. Gates the salary[] + note (remark[]) sections of the employee
// editor, and the whole Salary + Recurring-Deduction tabs. `canViewSalary` is an
// alias kept for call-site readability. Re-enforced by _middleware.js on the
// /api/hr/salary*, employee-salary, employee-recurring, employee-remark, contract
// proxies.
const HR_ROLES = new Set(['hr', 'admin'])

export function isHR(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => HR_ROLES.has(normRole(r)))
}

export function canViewSalary(me) {
  return isHR(me)
}

// Who may edit เกณฑ์การประเมิน — the matrix saying which positions are evaluated by which
// groups, on which questionnaire, at what weight. That is company-wide performance policy
// rather than a personnel record, so GM sits alongside HR and Admin here even though GM is
// outside HR_ROLES everywhere else. Re-checked in Xano (evaluation/rule) and in
// _middleware.js on /api/hr/evaluation/rule — keep the three in sync.
const EVAL_RULE_ROLES = new Set(['hr', 'admin', 'gm'])

export function canEditEvaluationRules(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => EVAL_RULE_ROLES.has(normRole(r)))
}

// Who may open ONE ผู้ประเมิน's paper — the per-evaluator breakdown behind a score, with its
// free-text comments. GM / HR / Admin, per the owner's brief (2026-09-14). A plain branch
// Manager reads the report and the per-round group averages, but not who wrote what.
//
// ⚠️ PRESENTATION ONLY. Xano's GET /evaluation/result/{id}/detail admits `is_hr ||
// is_manager`, and is_manager is true for a plain Manager, so this narrows the SCREEN and not
// the endpoint. Tighten the precondition there too if it has to actually hold.
//
// ⚠️ This does NOT hand GM peer names. Peer rows arrive from Xano already replaced with
// "เพื่อนร่วมงาน — ไม่ระบุชื่อ" for everyone except HR/Admin, and that stays (owner's
// decision, 2026-09-14): `gm` is six people — the two owners AND four branch managers — so
// widening it there would give a branch manager exactly the attribution the anonymity rule
// exists to deny. The name never reaches the browser; there is nothing here to un-hide.
const EVALUATOR_DETAIL_ROLES = new Set(['gm', 'hr', 'admin'])

export function canViewEvaluatorDetail(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => EVALUATOR_DETAIL_ROLES.has(normRole(r)))
}

// A Lab-only session: the person's only functional (employee-table) role is "Lab".
// They still carry the base "employee" role so they can log in, but hold no other
// functional role (Manager/Counter/Doctor/…). These sessions see ONLY the Lab menu,
// are sent to /lab on login, and can set only the 4 lab-stage statuses. Someone who
// ALSO holds another functional role keeps the full app. The "Lab" role comes from
// the employee record's `role` enum (values: Admin/Assistance/Doctor/HR/Counter/Lab/
// GM/Manager/Stock Manager). Mirrored server-side in functions/_middleware.js — keep in sync.
const NON_LAB_FUNCTIONAL_ROLES = new Set(['admin', 'manager', 'gm', 'stockmanager', 'doctor', 'counter', 'hr', 'assistance'])

export function isLabOnly(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  const norm = roles.map(normRole)
  return norm.includes('lab') && !norm.some((r) => NON_LAB_FUNCTIONAL_ROLES.has(r))
}

// Which lab stages a session sees (Ram, 2026-10-18) — consumed by lib/lab.js#stagesForScope.
//   'lab'   — a Lab-only technician: ONLY the 4 bench stages (2–5), and only those rows.
//   'full'  — Manager / GM / Admin: every stage, unmasked. Stock Manager is deliberately
//             OUT (it is in canManage's set, but Ram named these three).
//   'staff' — everyone else: every stage, but LAB รับงาน (3) and LAB ทำงานเสร็จ (4) read as
//             one merged "อยู่ที่ LAB" — they are the lab's internal steps, not the clinic's.
const FULL_STAGE_ROLES = new Set(['manager', 'gm', 'admin'])

export function labStageScope(me) {
  if (isLabOnly(me)) return 'lab'
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => FULL_STAGE_ROLES.has(normRole(r))) ? 'full' : 'staff'
}

// An "agency" session — an external marketing/agency account (role `agency`,
// NOT `employee`). Allowed to log in, then confined to the Statistic + Call
// pages only (no patient data, no clinical/manager access). Someone who ALSO
// holds the employee role keeps the full app and is not "agency-only".
// Mirrors functions/_middleware.js#isAgencyOnly — keep the two in sync.
export function isAgencyOnly(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.includes('agency') && !roles.includes('employee')
}

// Who may write a ปรับ (adjustment) stock row. เบิกใช้ / รับเพิ่ม are open to any
// employee — they record what physically happened. ปรับ overrides the ledger to
// an arbitrary number in either direction, so it is Stock Manager / Admin only.
// Re-checked server-side in functions/api/stock.js; this copy shapes the UI.
const STOCK_ADJUST_ROLES = new Set(['stockmanager', 'admin'])

export function canAdjustStock(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => STOCK_ADJUST_ROLES.has(normRole(r)))
}

// Who may edit/delete a stock row dated BEFORE today. Any employee can fix a
// same-day slip (they were there; the count is still in the room), but a past
// row has already been counted into reports — reopening it is a Stock Manager /
// Admin call. Same role set as canAdjustStock today, kept as a separate rule
// because they answer different questions and may diverge.
const STOCK_PAST_EDIT_ROLES = new Set(['stockmanager', 'admin'])

export function canEditPastStock(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => STOCK_PAST_EDIT_ROLES.has(normRole(r)))
}

// Who may edit or delete an item in the inventory MASTER (คลังของ) — rename,
// change unit, swap the picture, remove the item. Recording a movement is open
// to any employee (they report what physically happened), but the master is
// what every movement row, report and reorder point is keyed on, so rewriting
// it is Stock Manager / Admin. Re-checked in functions/api/inventory.js — this
// copy shapes the UI.
const INVENTORY_MANAGE_ROLES = new Set(['stockmanager', 'admin'])

export function canManageInventory(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.some((r) => INVENTORY_MANAGE_ROLES.has(normRole(r)))
}

// Who may CREATE a new chart row for a chartless treatment: doctors and
// employees holding the Admin role. Plain front-office employees can still edit
// existing charts (canEditClinical) but not create new ones.
export function canCreateChart(me) {
  return isDoctor(me) || isAdmin(me)
}

/* -------------------------------------------------------------------------- */
/* Per-section chart permissions                                              */
/* -------------------------------------------------------------------------- */
//
// Rules (from user spec):
//   • Employee  — may edit every section EXCEPT the clinical record. May edit
//                 the clinical record only when the treatment branch is Tooth
//                 Taxi (or the employee is an Admin).
//   • Doctor    — may edit every section EXCEPT the appointment, and ONLY on a
//                 treatment that is their own (treatment.doctor_id === their
//                 Doctor id). A doctor on another doctor's case sees it as
//                 read-only.
//   • Clinical record (Clinical + Full Mouth Exam) is additionally LOCKED once
//                 the treatment day has passed — for everyone — UNLESS the user
//                 is an Admin or the treatment branch is Tooth Taxi.
//   • A user holding both roles gets the UNION of their grants (full access).

// The two cards that make up the "clinical record".
const CLINICAL_SECTIONS = new Set(['clinical', 'oral-exam'])

// branch_id may live on the treatment, its embedded appointment (appointmentlist),
// or the chart. Returns the first numeric branch id found, or NaN.
export function resolveBranchId(treatment, chart) {
  const al = treatment?.appointmentlist || treatment?.appointmentList || {}
  const candidates = [
    treatment?.branch_id, treatment?.branch?.id,
    al?.branch_id, al?.branch?.id,
    chart?.branch_id, chart?.branch?.id,
    chart?.appointmentlist?.branch_id, chart?.appointmentList?.branch_id,
  ]
  for (const c of candidates) {
    if (c != null && c !== '') {
      const n = Number(c)
      if (!Number.isNaN(n)) return n
    }
  }
  return NaN
}

export function isToothTaxiTreatment(treatment, chart) {
  return resolveBranchId(treatment, chart) === TOOTH_TAXI_BRANCH_ID
}

// The Doctor-table id this treatment belongs to (or null).
export function treatmentDoctorId(treatment) {
  const v = treatment?.doctor_id ?? treatment?.doctor?.id ?? null
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : null
}

// True only when the logged-in doctor's own Doctor id matches the treatment's.
// Requires a real doctor_id on the session — if absent we cannot confirm
// ownership and fail closed.
export function doctorOwnsTreatment(me, treatment) {
  const mine = Number(me?.doctor_id) || 0
  const owner = treatmentDoctorId(treatment)
  return mine > 0 && owner != null && mine === owner
}

// Same local calendar day? Compares by local Y-M-D (not UTC) so it is correct
// in Asia/Bangkok where a date-only "YYYY-MM-DD" parses to local midnight.
function localYMD(d) {
  if (!d) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function isTreatmentDay(treatment, now = new Date()) {
  const d = parseDateObj(treatment?.date ?? treatment?.treatment_date)
  if (!d) return false
  return localYMD(d) === localYMD(now)
}

// Is the clinical record locked by the time rule for this user/treatment?
// (Independent of the role grant — used for the "why is this locked" note.)
export function isClinicalTimeLocked(me, treatment, chart) {
  if (isAdmin(me)) return false
  if (isToothTaxiTreatment(treatment, chart)) return false
  return !isTreatmentDay(treatment)
}

/**
 * Full state of the clinical record (Clinical + Full Mouth Exam) for this
 * user/treatment, so the UI can both gate AND explain the lock.
 *
 * @returns {{ editable: boolean, reason: null | 'role' | 'time' }}
 *   reason 'role' → no role grant at all (regular employee / non-owning doctor)
 *   reason 'time' → role allows, but the treatment day has passed
 */
export function clinicalEditState(me, treatment, chart) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  const emp   = roles.includes('employee')
  const doc   = roles.includes('doctor')
  const admin = roles.includes('admin')
  const toothTaxi = isToothTaxiTreatment(treatment, chart)

  // Role grant for the clinical record, IGNORING the time lock.
  let roleGrants = false
  if (emp && (toothTaxi || admin)) roleGrants = true       // employee: only ToothTaxi / Admin
  if (doc && doctorOwnsTreatment(me, treatment)) roleGrants = true  // doctor: own treatment

  if (!roleGrants) return { editable: false, reason: 'role' }
  if (isClinicalTimeLocked(me, treatment, chart)) return { editable: false, reason: 'time' }
  return { editable: true, reason: null }
}

/**
 * Can the current user edit a given chart section for this treatment?
 *
 * @param section  'appointment' | 'treatment' | 'clinical' | 'oral-exam'
 *                 | 'documents' | 'consent' | 'file'
 */
export function canEditSection(section, me, treatment, chart) {
  if (!me) return false

  // Clinical record (Clinical + Full Mouth Exam) — role grant + time lock.
  if (CLINICAL_SECTIONS.has(section)) {
    return clinicalEditState(me, treatment, chart).editable
  }

  const roles = Array.isArray(me.roles) ? me.roles : []
  const emp   = roles.includes('employee')
  const doc   = roles.includes('doctor')

  // Employees may edit every non-clinical section, appointment included.
  if (emp) return true

  // A doctor may edit any non-clinical section EXCEPT the appointment, and ONLY
  // on their own treatment. A non-owning doctor → whole case read-only.
  if (doc && section !== 'appointment' && doctorOwnsTreatment(me, treatment)) {
    return true
  }

  return false
}
